import { api } from '../api.js';
import { h } from '../dom.js';
import { go, guardNavigation, workspacePath } from '../router.js';
import { groupsOf, VIEWS, normalized, dateFormat } from './groups.js';
import { w, language, error } from './words.js';

/** The collection and the reader/editor share one mounted element, so polling cannot replace a draft.
 * @param {{ wsId: string, documentId?: string, workspace: any, user: any }} options */
export async function docsTab({ wsId, documentId, workspace, user }) {
  const base = `/api/ws/${wsId}/docs`, route = workspacePath(wsId, `/docs`);
  const el = h('section', { class: 'docs' });
  const preference = `ws-docs-view:${user.id}:${wsId}`;
  const params = new URLSearchParams(location.search);
  let preferred = 'folders';
  try { preferred = localStorage.getItem(preference) || preferred; } catch {}
  let view = VIEWS.includes(params.get('view')) ? params.get('view') : VIEWS.includes(preferred) ? preferred : 'folders';
  let group = params.has('group') ? params.get('group') : null;
  let collection = await api('GET', base), ws = workspace, doc = null;
  let editing = null, folderForm = null, dirty = false, saving = false, disposed = false, search = '';
  let failure = '', status = '', conflict = false;
  const canEdit = () => ws.me.level !== 'member';
  const count = n => n.toLocaleString(language() === 'fa' ? 'fa-IR' : 'en-US');
  const date = value => dateFormat(language(), user.cal, { year: 'numeric', month: 'long', day: 'numeric' }).format(value);
  const query = () => {
    const q = new URLSearchParams({ view });
    if (group !== null) q.set('group', group);
    return '?' + q;
  };
  const listURL = () => route + query();
  const openURL = id => `${route}/${id}${query()}`;
  const button = (label, onclick, cls = '') => h('button', { type: 'button', class: 'btn ' + cls, onclick }, label);
  const note = () => h('div', { class: 'docs-notes' },
    h('p', { class: 'error', role: 'alert' }, failure),
    h('p', { class: 'muted small', role: 'status' }, status));
  const mayLeave = () => !saving && (!dirty || confirm(w('discard')));
  const cleanupGuard = guardNavigation(mayLeave);
  const beforeUnload = e => { if (dirty || saving) { e.preventDefault(); e.returnValue = ''; } };
  addEventListener('beforeunload', beforeUnload);

  const grouping = () => groupsOf(collection.documents, collection.folders, view, {
    lang: language(), calendar: user.cal, unfiled: w('unfiled'), untagged: w('untagged'),
  });
  function updateURL() {
    history.replaceState(history.state, '', documentId ? openURL(documentId) : listURL());
    try { localStorage.setItem(preference, view); } catch {}
  }
  function choose(nextView, nextGroup = null) {
    if (folderForm && dirty && !confirm(w('discard'))) return;
    folderForm = null; dirty = false; view = nextView; group = nextGroup; failure = ''; status = '';
    updateURL(); drawList();
  }
  function docRow(d) {
    return h('a', { class: 'doc-row', href: openURL(d.id), 'data-link': true },
      h('div', { class: 'row spread' }, h('strong', {}, d.title), h('time', { class: 'muted small', datetime: new Date(d.updated_at).toISOString() }, date(d.updated_at))),
      (d.subtitle || d.excerpt) && h('p', { class: 'doc-excerpt muted' }, d.subtitle || d.excerpt),
      d.tags.length > 0 && h('div', { class: 'row' }, ...d.tags.map(tag => h('span', { class: 'doc-tag' }, '#' + tag))));
  }
  function listBody(container) {
    const groups = grouping();
    if (group !== null && !groups.some(g => g.key === group)) { group = null; updateURL(); }
    const needle = normalized(search);
    const visible = (group === null ? groups : groups.filter(g => g.key === group)).map(g => ({ ...g,
      documents: g.documents.filter(d => !needle || normalized([d.title, d.subtitle, ...d.tags].join(' ')).includes(needle)),
    }));
    const nonempty = visible.filter(g => g.documents.length);
    container.replaceChildren();
    if (!nonempty.length) {
      container.append(h('div', { class: 'docs-empty' }, h('h2', {}, search ? w('noMatch') : w('empty')),
        !search && h('p', { class: 'muted' }, canEdit() ? w('emptyHint') : w('readOnly')),
        !search && canEdit() && button(w('newDoc'), () => go(openURL('new')), 'primary')));
      return;
    }
    for (const g of nonempty) container.append(h('section', { class: 'doc-group', 'data-group': g.key },
      h('h2', { class: 'doc-group-title' }, g.name, h('span', { class: 'count' }, count(g.documents.length))),
      ...g.documents.map(docRow)));
  }
  function drawList() {
    const groups = grouping(), content = h('div', { class: 'docs-list' });
    const searchBox = h('input', { type: 'search', value: search, placeholder: w('search'), 'aria-label': w('search'),
      oninput: () => { search = searchBox.value; listBody(content); } });
    const views = h('div', { class: 'docs-views', role: 'group', 'aria-label': w('view') },
      ...VIEWS.map(v => h('button', { type: 'button', class: 'btn' + (view === v ? ' selected' : ''), 'aria-pressed': String(view === v),
        onclick: () => choose(v) }, w(v === 'folders' ? 'foldersView' : v === 'tags' ? 'tagsView' : 'dateView'))));
    const filters = h('nav', { class: 'docs-groups', 'aria-label': w('view') });
    const groupButton = (key, name, total) => h('button', { type: 'button', class: 'btn doc-filter' + (group === key ? ' selected' : ''),
      'aria-pressed': String(group === key), onclick: () => choose(view, key) }, h('span', {}, name), h('span', { class: 'count' }, count(total)));
    filters.append(groupButton(null, w('all'), collection.documents.length));
    for (const g of groups) filters.append(groupButton(g.key, g.name, g.documents.length));
    if (canEdit() && view === 'folders') {
      filters.append(button('+ ' + w('newFolder'), () => { folderForm = { id: null, name: '' }; dirty = false; drawList(); }, 'quiet'));
      if (group) {
        const f = collection.folders.find(x => x.id === group);
        if (f) filters.append(h('div', { class: 'row' },
          button(w('rename'), () => { folderForm = { ...f }; dirty = false; drawList(); }, 'quiet small'),
          button(w('deleteFolder'), async () => {
            if (!confirm(w('deleteFolderConfirm'))) return;
            saving = true;
            try { await api('DELETE', `${base}/folders/${f.id}`); collection = await api('GET', base); group = null; updateURL(); failure = ''; }
            catch (e) { failure = error(e); }
            finally { saving = false; if (!disposed) drawList(); }
          }, 'quiet small danger')));
      }
    }
    el.replaceChildren(
      h('div', { class: 'row spread docs-heading' }, h('h2', {}, w('docs')), canEdit() && button('+ ' + w('newDoc'), () => go(openURL('new')), 'primary')),
      h('div', { class: 'docs-toolbar' }, views, searchBox),
      view === 'date' && h('p', { class: 'muted small' }, w('dateHint')),
      note(),
      h('div', { class: 'docs-layout' }, h('aside', {}, filters, folderForm && folderEditor()), content));
    listBody(content);
  }
  function folderEditor() {
    const f = folderForm;
    const input = h('input', { required: true, maxlength: 80, value: f.name, 'aria-label': w('folderName'), placeholder: w('folderName'),
      oninput: () => { f.name = input.value; dirty = true; } });
    const submit = h('button', { type: 'submit', class: 'btn primary' }, w('save'));
    const form = h('form', { class: 'docs-folder-form stack', onsubmit: async e => {
      e.preventDefault(); if (saving) return;
      saving = true; form.inert = true;
      try {
        const result = await api(f.id ? 'PATCH' : 'POST', base + '/folders' + (f.id ? '/' + f.id : ''), { name: input.value });
        dirty = false; folderForm = null; group = result.id; failure = ''; updateURL();
        collection = await api('GET', base);
      } catch (e) { failure = error(e); }
      finally { saving = false; if (!disposed) drawList(); }
    } }, input, h('div', { class: 'row' }, submit, button(w('cancel'), () => {
      if (dirty && !confirm(w('discard'))) return;
      folderForm = null; dirty = false; failure = ''; drawList();
    }, 'quiet')));
    return form;
  }

  function editable(tag, value, label, multiline = false) {
    return h(tag, { class: 'doc-editable', contenteditable: 'plaintext-only', role: 'textbox', 'aria-label': label,
      'aria-multiline': String(multiline), 'data-placeholder': label, spellcheck: true,
      oninput: () => { dirty = true; }, onkeydown: e => { if (!multiline && e.key === 'Enter') e.preventDefault(); } }, value);
  }
  function startEdit() {
    editing = { title: doc?.title || '', subtitle: doc?.subtitle || '', body: doc?.body || '',
      folder_id: doc?.folder_id || (view === 'folders' && group && collection.folders.some(f => f.id === group) ? group : null),
      tags: [...(doc?.tags || (view === 'tags' && group ? [group] : []))] };
    dirty = false; failure = ''; status = ''; conflict = false; drawDocument();
    /** @type {HTMLElement} */ (el.querySelector('[contenteditable]'))?.focus();
  }
  let readEditor = () => editing;
  function drawDocument() {
    const editingNow = !!editing;
    const data = editing || doc;
    const article = h('article', { class: 'doc-article' + (editingNow ? ' editing' : '') });
    const back = h('a', { class: 'btn quiet', href: listURL(), 'data-link': true }, '‹ ' + w('back'));
    if (!data) { el.replaceChildren(back, h('p', { class: 'muted empty' }, w('missing'))); return; }
    document.title = data.title || w('untitled');
    let title, subtitle, body, folder, tags;
    if (editingNow) {
      title = editable('h1', data.title, w('title'));
      subtitle = editable('p', data.subtitle, w('subtitle'));
      body = editable('div', data.body, w('body'), true); body.classList.add('doc-body');
      folder = h('select', { 'aria-label': w('folder'), onchange: () => { dirty = true; } },
        h('option', { value: '' }, w('unfiled')), ...collection.folders.map(f => h('option', { value: f.id }, f.name)));
      folder.value = data.folder_id || '';
      tags = h('input', { value: data.tags.join('، '), 'aria-label': w('tagsLabel'), placeholder: w('tagsLabel'),
        oninput: () => { dirty = true; } });
      const readTags = () => tags.value.split(/[,،\n]/).map(t => t.trim()).filter(Boolean);
      readEditor = () => ({ title: title.innerText, subtitle: subtitle.innerText, body: body.innerText,
        folder_id: folder.value || null, tags: readTags() });
      article.append(title, subtitle, body, h('div', { class: 'doc-properties stack' },
        h('label', {}, w('folder'), folder), h('label', {}, w('tagsLabel'), tags),
        h('p', { class: 'muted small' }, w('tagsHelp')),
        h('div', { class: 'row doc-tag-suggestions' }, ...[...new Set(collection.documents.flatMap(d => d.tags))].slice(0, 24).map(tag =>
          button('#' + tag, () => { const current = readTags(); if (!current.includes(tag)) { tags.value = [...current, tag].join('، '); dirty = true; } }, 'small quiet')))));
    } else {
      article.append(h('h1', {}, data.title), data.subtitle && h('p', { class: 'doc-subtitle muted' }, data.subtitle),
        h('div', { class: 'doc-body' }, data.body),
        h('footer', { class: 'doc-properties stack' },
          h('div', { class: 'row' }, button(collection.folders.find(f => f.id === data.folder_id)?.name || w('unfiled'), () => {
            view = 'folders'; group = data.folder_id || ''; go(listURL());
          }, 'quiet small'), ...data.tags.map(tag => button('#' + tag, () => { view = 'tags'; group = tag; go(listURL()); }, 'quiet small'))),
          h('p', { class: 'muted small' }, `${w('updated')}: ${date(data.updated_at)} · ${w('created')}: ${date(data.created_at)}`)));
    }
    const notes = note();
    const showNotes = () => notes.replaceChildren(...note().childNodes);
    const saveButton = button(w('save'), async () => {
      if (saving) return;
      const payload = readEditor();
      saving = true; article.inert = true; actions.inert = true;
      failure = ''; status = w('saving'); showNotes();
      try {
        const result = await api(doc ? 'PUT' : 'POST', doc ? `${base}/${doc.id}` : base, { ...payload, ...(doc ? { ver: doc.ver } : {}) });
        doc = result.document; documentId = doc.id; editing = null; dirty = false; conflict = false; status = w('saved');
        updateURL();
        collection = await api('GET', base);
      } catch (e) { failure = error(e); status = ''; conflict = e.message === 'doc_conflict'; }
      finally {
        saving = false;
        if (!disposed) {
          if (editing) {
            // Never redraw the draft after a failed save, even when the network is down.
            article.inert = false; actions.inert = false; showNotes();
            latestButton.hidden = !conflict;
          } else drawDocument();
        }
      }
    }, 'primary');
    const latestButton = button(w('latest'), async () => {
      if (!mayLeave()) return;
      saving = true;
      try {
        const fresh = await api('GET', `${base}/${doc.id}`);
        collection = await api('GET', base); doc = fresh.document; editing = null; dirty = false; failure = ''; conflict = false;
        if (!disposed) drawDocument();
      } catch (e) { failure = error(e); showNotes(); }
      finally { saving = false; }
    }, 'quiet');
    latestButton.hidden = !conflict;
    const actions = h('div', { class: 'doc-actions' });
    if (editingNow) actions.append(saveButton, button(w('cancel'), () => {
      if (!mayLeave()) return;
      editing = null; dirty = false; failure = ''; status = ''; conflict = false;
      if (doc) drawDocument(); else go(listURL());
    }, 'quiet'), button(w('copyDraft'), async () => {
      const d = readEditor();
      try { await navigator.clipboard.writeText([d.title, d.subtitle, d.body, d.tags.join(', ')].filter(Boolean).join('\n\n')); status = w('copied'); }
      catch { status = w('copyFailed'); }
      showNotes();
    }, 'quiet small'), latestButton);
    else if (canEdit()) actions.append(button(w('edit'), startEdit, 'primary'), button(w('delete'), async () => {
      if (saving || !confirm(w('deleteConfirm'))) return;
      saving = true; actions.inert = true;
      try { await api('DELETE', `${base}/${doc.id}`, { ver: doc.ver }); saving = false; go(listURL()); }
      catch (e) { failure = error(e); showNotes(); }
      finally { saving = false; actions.inert = false; }
    }, 'quiet danger'));
    el.replaceChildren(back, article, notes, actions);
  }

  async function reload() {
    if (editing || folderForm || saving) return false;
    const fresh = await api('GET', base);
    if (disposed) return false;
    if (editing || folderForm || saving) return false;
    const changed = fresh.version !== collection.version;
    if (documentId && documentId !== 'new') {
      let nextDoc;
      try { nextDoc = (await api('GET', `${base}/${documentId}`)).document; }
      catch (e) { if (e.status === 404) nextDoc = null; else throw e; }
      if (disposed || editing || saving) return false;
      doc = nextDoc; collection = fresh; drawDocument();
    } else if (!disposed && !folderForm && !saving) { collection = fresh; drawList(); }
    return changed;
  }
  try {
    if (documentId === 'new') {
      if (canEdit()) startEdit();
      else { documentId = null; updateURL(); drawList(); }
    } else if (documentId) {
      try { doc = (await api('GET', `${base}/${documentId}`)).document; }
      catch (e) { if (e.status !== 404) throw e; }
      drawDocument();
    } else drawList();
  } catch (e) { cleanupGuard(); removeEventListener('beforeunload', beforeUnload); throw e; }
  return {
    el, reload, version: () => collection.version, busy: () => !!editing || !!folderForm || saving,
    setWorkspace: next => { ws = next; },
    destroy: () => { disposed = true; cleanupGuard(); removeEventListener('beforeunload', beforeUnload); },
  };
}
